import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Form, Spin } from "antd"
import { LoadingOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Modal } from "ui/Modal"
import { CustomForm } from "ui/Form"
import { toaster } from "components/common/Toast"
import { getGuideClient, saveClient } from "store/clients"
import ClientForm from "pagesV2/pro/clients/ClientWindow/ClientForm"
import { buildClientInitialValues, buildGuideClientFormData, handleClientFormErrors } from "./clientFormUtils"

const EditClientModal = ({ clientId, open, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const client = useSelector((state) => state.clients.client)
  const [loading, setLoading] = useState(false)
  const isInitialSetRef = useRef(false)

  // Fetch the client details whenever the modal is opened for a given client.
  useEffect(() => {
    if (!open || !clientId) return
    isInitialSetRef.current = false
    setLoading(true)
    Promise.resolve(dispatch(getGuideClient(clientId))).finally(() => setLoading(false))

    return () => {
      form.resetFields()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, clientId])

  // Populate the form once the fetched client lands in the store.
  useEffect(() => {
    if (open && client?.id && client.user && !isInitialSetRef.current) {
      form.setFieldsValue(buildClientInitialValues(client))
      isInitialSetRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, open])

  const handleSubmit = async () => {
    try {
      const formValues = await form.validateFields()
      const invalidFields = form.getFieldsError().filter(({ errors }) => errors.length)
      if (invalidFields.length) {
        for (const { name } of invalidFields) {
          form.setFieldsTouched([name], true)
        }
        return
      }
      setLoading(true)
      const formData = buildGuideClientFormData(formValues, client, clientId)
      const response = await dispatch(saveClient(clientId, formData))
      if (response) {
        toaster.success(t("client.client_updated", { name: formValues?.first_name }))
        onSuccess?.()
        onClose()
      } else {
        setLoading(false)
      }
    } catch (err) {
      handleClientFormErrors(err, clientId, form, t)
      setLoading(false)
    }
  }

  return (
    <Modal
      width={1000}
      title={t("client.edit")}
      open={open}
      onCancel={onClose}
      scrollable
      footer={
        <Flex gap={10} justify="flex-end">
          <Button key="cancel" color="primary" variant="outlined" onClick={onClose} size="large">
            {t("global.cancel")}
          </Button>
          <Button key="submit" type="primary" onClick={handleSubmit} size="large" loading={loading}>
            {t("global.save")}
          </Button>
        </Flex>
      }
    >
      <Spin spinning={loading} indicator={<LoadingOutlined spin />}>
        <CustomForm form={form} layout="vertical">
          <ClientForm form={form} />
        </CustomForm>
      </Spin>
    </Modal>
  )
}

EditClientModal.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
}

export default EditClientModal
